// src/services/kbDocumentsService.ts
//
// All Supabase access for the KB corpus (yak_kb_documents / _doc_versions /
// _chunks) is isolated here. Two write paths, per spine AD-3:
//   * Metadata reads/edits go DIRECT under RLS (admin config, not a ticket
//     transition — no tx_ RPC, no p_actor).
//   * Uploads delegate to the `kb-ingest` edge function (user JWT + RLS) so the
//     OpenAI key never reaches the browser and document + version 1 + chunks
//     are created atomically DB-side.
// Components/hooks consume the clean `src/types/yak.ts` types only; PostgREST
// results are cast once per method.

import { supabase } from '../lib/supabase'
import type { YakKbChunk, YakKbDocument, YakKbDocVersion } from '../types/yak'

const STALE_ROW_MESSAGE =
  'This document no longer exists — refresh and try again'
const EMPTY_TITLE_MESSAGE = 'Title cannot be empty'
// Content-immutability (Story 2.2): version + chunk rows are insert-only,
// enforced by a DB trigger (SQLSTATE 23001 / restrict_violation). No console
// path issues UPDATE/DELETE against them, so this is defense-in-depth — a
// trigger rejection must never surface as a raw Postgres string.
const IMMUTABLE_ROW_MESSAGE =
  'This content is versioned and can no longer be changed'
// Generic, retryable fallback for an upload that failed edge-side. "Nothing was
// saved" is true because yak_kb_create_document is atomic (all-or-none).
const UPLOAD_FALLBACK_MESSAGE =
  "The document couldn't be processed. Nothing was saved — try again."
// Replace failed edge-side: the previous version keeps serving (yak_kb_add_version
// is atomic — a partial/chunkless version never becomes latest). AC 4.
const REPLACE_FALLBACK_MESSAGE =
  "The new version couldn't be processed — the previous version is still active. Try again."

interface PostgrestErrorLike {
  message: string
  code?: string
}

// Exported for unit testing — maps the enumerable metadata-edit failures to
// stable human copy so a raw Postgres string never leaks. PGRST116 = 0 rows
// (the row was deleted/retired between load and save); 23514 = the title CHECK;
// 23001 = the content-immutability trigger.
export function throwMapped(error: PostgrestErrorLike) {
  if (error.code === 'PGRST116') throw new Error(STALE_ROW_MESSAGE)
  if (error.code === '23514') throw new Error(EMPTY_TITLE_MESSAGE)
  if (error.code === '23001') throw new Error(IMMUTABLE_ROW_MESSAGE)
  throw new Error(error.message)
}

// Ordered concatenation fallback for a version's content view. Story 2.1 stores
// content_text on every version row, so this is only reached if that text is
// ever absent; it reconstructs a readable view from the retrieval chunks in
// chunk_index order. Pure + exported for unit testing.
export function concatChunks(
  chunks: Pick<YakKbChunk, 'chunk_index' | 'content'>[]
): string {
  return [...chunks]
    .sort((a, b) => a.chunk_index - b.chunk_index)
    .map(c => c.content)
    .join('\n\n')
}

export interface KbMetadataEdit {
  title: string
  brand: string | null
  repShareable: boolean
}

export interface KbUploadInput {
  title: string
  brand: string | null
  repShareable: boolean
}

export interface KbUploadResult {
  documentId: string
  versionId: string
  chunkCount: number
}

class KbDocumentsService {
  // Whole org corpus (RLS scopes to the caller's org), newest-touched first so
  // a just-edited/-uploaded document surfaces at the top.
  async listDocuments(): Promise<YakKbDocument[]> {
    const { data, error } = await supabase
      .from('yak_kb_documents')
      .select('*')
      .order('updated_at', { ascending: false })

    if (error) throw new Error(error.message)
    return (data as unknown as YakKbDocument[] | null) || []
  }

  // Single document; null when it no longer exists (0 rows → PGRST116, mapped
  // to null per the repo convention rather than thrown).
  async getDocument(id: string): Promise<YakKbDocument | null> {
    const { data, error } = await supabase
      .from('yak_kb_documents')
      .select('*')
      .eq('id', id)
      .maybeSingle()

    if (error) throw new Error(error.message)
    return (data as unknown as YakKbDocument | null) ?? null
  }

  // Latest version = max(version_number) (recorded decision: no pointer col).
  // Powers the detail's read-only content preview.
  async getLatestVersion(documentId: string): Promise<YakKbDocVersion | null> {
    const { data, error } = await supabase
      .from('yak_kb_doc_versions')
      .select('*')
      .eq('document_id', documentId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) throw new Error(error.message)
    return (data as unknown as YakKbDocVersion | null) ?? null
  }

  // Metadata edit: direct UPDATE under RLS. Content-immutability applies to
  // content, not labels — no version is created; the updated_at trigger bumps.
  // Title is trimmed at the boundary so no caller can persist a blank name
  // before the DB CHECK does.
  async updateMetadata(
    id: string,
    edit: KbMetadataEdit
  ): Promise<YakKbDocument> {
    const title = edit.title.trim()
    if (!title) throw new Error(EMPTY_TITLE_MESSAGE)

    const { data, error } = await supabase
      .from('yak_kb_documents')
      .update({
        title,
        brand: edit.brand,
        rep_shareable: edit.repShareable,
      })
      .eq('id', id)
      .select('*')
      .single()

    if (error) throwMapped(error)
    return data as unknown as YakKbDocument
  }

  // Retire a document: a single direct-under-RLS status flip (AD-3 admin-config
  // write, not an AD-15 tx_ transition — no p_actor). Terminal at pilot: the DB
  // trigger refuses every later change to a retired row, un-retire included.
  // `.select('id').single()` makes a zero-row outcome detectable — PGRST116
  // means the row was removed/hidden between load and click, mapped to the
  // stale-row copy so the user refetches. A trigger rejection (the row was
  // already retired by someone else) arrives as P0001 and its message is the
  // user-facing copy written in the trigger, passed straight through by
  // throwMapped's default branch. No role gate here — FR-27, RLS is the boundary.
  async retireDocument(id: string): Promise<void> {
    const { error } = await supabase
      .from('yak_kb_documents')
      .update({ status: 'retired' })
      .eq('id', id)
      .select('id')
      .single()

    if (error) throwMapped(error)
  }

  // All versions of a document, newest-first — the history list (AC 5). Content
  // is not selected here (the list shows version number / timestamp / source
  // only); getVersionContent loads a single version's text on demand.
  async listVersions(documentId: string): Promise<YakKbDocVersion[]> {
    const { data, error } = await supabase
      .from('yak_kb_doc_versions')
      .select('*')
      .eq('document_id', documentId)
      .order('version_number', { ascending: false })

    if (error) throw new Error(error.message)
    return (data as unknown as YakKbDocVersion[] | null) || []
  }

  // A single version plus its readable content for the read-only version view
  // (AC 5). content_text is the source of truth (Story 2.1 stores it on every
  // version); if it is ever absent we fall back to ordered chunk concatenation
  // so the view is never blank. Returns null when the version is gone/hidden.
  async getVersionContent(
    versionId: string
  ): Promise<{ version: YakKbDocVersion; content: string } | null> {
    const { data, error } = await supabase
      .from('yak_kb_doc_versions')
      .select('*')
      .eq('id', versionId)
      .maybeSingle()

    if (error) throw new Error(error.message)
    if (!data) return null
    const version = data as unknown as YakKbDocVersion

    if (version.content_text && version.content_text.trim()) {
      return { version, content: version.content_text }
    }

    // Fallback: reconstruct from the version's chunks in order.
    const { data: chunkRows, error: chunkErr } = await supabase
      .from('yak_kb_chunks')
      .select('chunk_index, content')
      .eq('document_version_id', versionId)
      .order('chunk_index', { ascending: true })

    if (chunkErr) throw new Error(chunkErr.message)
    const content = concatChunks(
      (chunkRows as unknown as
        | Pick<YakKbChunk, 'chunk_index' | 'content'>[]
        | null) || []
    )
    return { version, content }
  }

  // Upload delegates to kb-ingest (multipart FormData). The edge function
  // extracts/chunks/embeds, uploads the original, and commits atomically.
  async uploadDocument(
    file: File,
    input: KbUploadInput
  ): Promise<KbUploadResult> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', input.title.trim())
    if (input.brand) formData.append('brand', input.brand)
    formData.append('rep_shareable', String(input.repShareable))

    return this.invokeIngest(formData, UPLOAD_FALLBACK_MESSAGE)
  }

  // Replace content: delegate to the SAME kb-ingest pipeline in "replace mode"
  // (a document_id field). The edge function chunks/embeds the new content and
  // calls yak_kb_add_version, which publishes a new immutable version atomically
  // (AC 1, 4). Metadata is untouched. The service never calls OpenAI.
  async replaceContent(
    documentId: string,
    file: File
  ): Promise<KbUploadResult> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('document_id', documentId)

    return this.invokeIngest(formData, REPLACE_FALLBACK_MESSAGE)
  }

  // Shared kb-ingest invocation + error unwrapping for both modes.
  private async invokeIngest(
    formData: FormData,
    fallbackMessage: string
  ): Promise<KbUploadResult> {
    const { data, error } = await supabase.functions.invoke('kb-ingest', {
      body: formData,
    })

    if (error) {
      throw new Error(await unwrapInvokeError(error, fallbackMessage))
    }
    if (!data || data.success === false) {
      throw new Error(data?.error || fallbackMessage)
    }

    return {
      documentId: data.document_id,
      versionId: data.version_id,
      chunkCount: data.chunk_count,
    }
  }
}

// A non-2xx from functions.invoke yields a FunctionsHttpError whose `message`
// is the useless "Edge Function returned a non-2xx status code". The function's
// real (mapped) error string lives in the JSON body on error.context (a
// Response); recover it, falling back to the caller's generic retryable copy.
async function unwrapInvokeError(
  error: unknown,
  fallbackMessage: string
): Promise<string> {
  const ctx = (error as { context?: unknown }).context
  if (ctx && typeof (ctx as Response).json === 'function') {
    try {
      const body = await (ctx as Response).json()
      if (body && typeof body.error === 'string' && body.error) {
        return body.error
      }
    } catch {
      // Body wasn't JSON / already consumed — fall through to generic copy.
    }
  }
  return fallbackMessage
}

export const kbDocumentsService = new KbDocumentsService()
